/**
 * Shared hook for My Tasks data
 * Used by both MyTasksWidget and MyTasksTab to ensure consistent data
 */

import { getErrorStatus } from '@/lib/api/error-utils'
import { tasksApi } from '@/lib/api/tasks.api'
import { isTaskClosed } from '@/lib/tasks/task-completion'
// Store-based auth (not the AuthProvider context): this hook also runs in
// the Benny companion window, which has no AuthProvider. The store mirrors
// the context user in the main window (synced by MainLayout).
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { TaskWithAssignments } from '@scrambl/shared'
import { ModuleTypeEnum } from '@scrambl/shared'

const MY_TASKS_FETCH_LIMIT = 500

export interface UseMyTasksDataOptions {
  enabled?: boolean
  staleTime?: number
  gcTime?: number
  includeCompleted?: boolean
  /** Reuse the dashboard complete-list query; fall back if its first page is truncated. */
  reuseCompletedList?: boolean
}

export interface UseMyTasksDataResult {
  tasks: TaskWithAssignments[]
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refresh: () => Promise<void>
  invalidate: () => Promise<void>
}

// Stable query key for my tasks - shared across all consumers
// Include a version segment to prevent collisions with older cache shapes.
export const MY_TASKS_QUERY_KEY = ['my-tasks', 'v2'] as const

export interface MyTasksQueryScope {
  userId: string
  includeCompleted: boolean
  organizationId: string
}

/** Preserve the shared prefix and user/filter positions; legacy ownerless keys are not reused. */
export function getMyTasksQueryKey({
  userId,
  includeCompleted,
  organizationId,
}: {
  userId: string | undefined
  includeCompleted: boolean
  organizationId: string | undefined
}) {
  return [
    ...MY_TASKS_QUERY_KEY,
    userId,
    includeCompleted,
    organizationId,
  ] as const
}

export function getMyTasksQueryScope(
  key: readonly unknown[],
): MyTasksQueryScope | undefined {
  if (
    key[0] !== MY_TASKS_QUERY_KEY[0] ||
    key[1] !== MY_TASKS_QUERY_KEY[1] ||
    typeof key[2] !== 'string' ||
    !key[2] ||
    typeof key[3] !== 'boolean' ||
    typeof key[4] !== 'string' ||
    !key[4]
  )
    return undefined
  return { userId: key[2], includeCompleted: key[3], organizationId: key[4] }
}

export function getMyTaskAssigneeIds(
  task: Partial<TaskWithAssignments> | undefined,
): string[] {
  if (!task) return []
  const ids = new Set<string>()
  for (const raw of task.assignees ?? []) {
    if (typeof raw === 'string') {
      ids.add(raw)
      continue
    }
    if (!raw || typeof raw !== 'object') continue
    const assignee = raw as unknown as { id?: string; user?: { id?: string } }
    const id = assignee.user?.id ?? assignee.id
    if (id) ids.add(id)
  }
  for (const assignment of task.taskAssignments ?? []) {
    const id = assignment.userId ?? assignment.user?.id
    if (id) ids.add(id)
  }
  return [...ids]
}

/** A creation can enter only caches whose server-side My Tasks filters it satisfies. */
export function taskMatchesMyTasksQuery(
  key: readonly unknown[],
  task: Partial<TaskWithAssignments>,
): boolean {
  const scope = getMyTasksQueryScope(key)
  if (!scope || task.organizationId !== scope.organizationId) return false
  if (!scope.includeCompleted && isTaskClosed(task)) return false
  const isPersonalOwner =
    task.moduleType === ModuleTypeEnum.MEMBER && task.moduleId === scope.userId
  return isPersonalOwner || getMyTaskAssigneeIds(task).includes(scope.userId)
}

export function myTasksQueryOptions({
  userId,
  organizationId,
  includeCompleted,
}: Parameters<typeof getMyTasksQueryKey>[0]) {
  return {
    queryKey: getMyTasksQueryKey({ userId, organizationId, includeCompleted }),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!userId || !organizationId) {
        return { tasks: [], totalCount: 0 }
      }

      const params: Record<string, unknown> = {
        page: 1,
        limit: MY_TASKS_FETCH_LIMIT,
        // Keep newest updates in the first page so recently created tasks
        // are not pushed out when users have >limit historical tasks.
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        assigneeIds: [userId],
        excludeCompleted: !includeCompleted,
        // Work under a closed project or parent task never shows on My Work,
        // even in the Done lane that the completed tasks above feed.
        excludeClosedContainers: true,
        includePersonalTasks: true,
        includeSubtasks: true, // Include subtasks assigned to the user
        view: 'list',
      }

      const response = await tasksApi.getTasks(params, { signal })

      return {
        tasks: response.data || [],
        totalCount: response.meta?.totalItems || 0,
      }
    },
    staleTime: 30_000,
    gcTime: 10 * 60 * 1000,
  }
}

export function useMyTasksData(
  options: UseMyTasksDataOptions = {},
): UseMyTasksDataResult {
  const {
    enabled = true,
    staleTime = 30000,
    gcTime = 10 * 60 * 1000,
    includeCompleted = false,
    reuseCompletedList = false,
  } = options

  const { user } = useAuth()
  const currentUserId = user?.id
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const queryClient = useQueryClient()

  const primary = useQuery({
    ...myTasksQueryOptions({
      userId: currentUserId,
      organizationId,
      includeCompleted: includeCompleted || reuseCompletedList,
    }),

    enabled: enabled && !!currentUserId && !!organizationId,
    staleTime,
    gcTime,
    // Freshness comes from realtime cache updates + mutation writes; the global
    // refetch-on-focus (when stale) covers missed socket events without
    // refetching on every mount.
    refetchOnReconnect: true,
  })

  const needsOpenPage =
    reuseCompletedList &&
    !includeCompleted &&
    !!primary.data &&
    primary.data.totalCount > primary.data.tasks.length
  const openPage = useQuery({
    queryKey: getMyTasksQueryKey({
      userId: currentUserId,
      includeCompleted: false,
      organizationId,
    }),
    queryFn: async ({ signal }) => {
      const response = await tasksApi.getTasks(
        {
          page: 1,
          limit: MY_TASKS_FETCH_LIMIT,
          sortBy: 'updatedAt',
          sortOrder: 'desc',
          assigneeIds: [currentUserId!],
          excludeCompleted: true,
          includePersonalTasks: true,
          includeSubtasks: true,
          view: 'list',
        },
        { signal },
      )
      return {
        tasks: response.data || [],
        totalCount: response.meta?.totalItems || 0,
      }
    },
    enabled: enabled && !!currentUserId && !!organizationId && needsOpenPage,
    staleTime,
    gcTime,
  })
  const selected = needsOpenPage ? openPage : primary
  const { isLoading, isFetching, error, refetch } = selected
  const data =
    reuseCompletedList && !includeCompleted && !needsOpenPage && primary.data
      ? {
          tasks: primary.data.tasks.filter(task => !isTaskClosed(task)),
          totalCount: 0,
        }
      : selected.data
  if (data && reuseCompletedList && !includeCompleted && !needsOpenPage)
    data.totalCount = data.tasks.length

  const refresh = async () => {
    await refetch()
  }

  const invalidate = async () => {
    // Invalidate all my-tasks queries (with any includeCompleted value)
    await queryClient.invalidateQueries({ queryKey: MY_TASKS_QUERY_KEY })
  }

  return {
    tasks: [401, 403, 404].includes(getErrorStatus(error) ?? 0)
      ? []
      : data?.tasks || [],
    // No data yet is loading — not "no tasks" — even while the query is not
    // fetching: waiting for the user/org, or for the persisted cache to
    // restore (React Query's isLoading needs isFetching, so it reads false
    // there and the widgets fell through to "Nothing assigned to you").
    isLoading: isLoading || (enabled && selected.isPending),
    isFetching,
    error: error as Error | null,
    refresh,
    invalidate,
  }
}

// Helper to invalidate my tasks cache from anywhere
export function invalidateMyTasksCache(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  return queryClient.invalidateQueries({ queryKey: MY_TASKS_QUERY_KEY })
}

// Helper to update a task in the my tasks cache
export function updateTaskInMyTasksCache(
  queryClient: ReturnType<typeof useQueryClient>,
  taskId: string,
  updater: (task: TaskWithAssignments) => TaskWithAssignments,
) {
  queryClient.setQueriesData(
    { queryKey: MY_TASKS_QUERY_KEY },
    (
      oldData: { tasks: TaskWithAssignments[]; totalCount: number } | undefined,
    ) => {
      if (!oldData?.tasks) return oldData
      return {
        ...oldData,
        tasks: oldData.tasks.map(task =>
          task.id === taskId ? updater(task) : task,
        ),
      }
    },
  )
}

// Helper to add a task to the my tasks cache
export function addTaskToMyTasksCache(
  queryClient: ReturnType<typeof useQueryClient>,
  task: TaskWithAssignments,
) {
  queryClient.setQueriesData(
    {
      queryKey: MY_TASKS_QUERY_KEY,
      predicate: query => taskMatchesMyTasksQuery(query.queryKey, task),
    },
    (
      oldData: { tasks: TaskWithAssignments[]; totalCount: number } | undefined,
    ) => {
      // Leave pending queries pending; their authenticated response owns initialization.
      if (
        !oldData?.tasks ||
        oldData.tasks.some(existing => existing.id === task.id)
      )
        return oldData
      return {
        ...oldData,
        tasks: [task, ...oldData.tasks],
        totalCount: oldData.totalCount + 1,
      }
    },
  )
}

// Helper to remove a task from the my tasks cache
export function removeTaskFromMyTasksCache(
  queryClient: ReturnType<typeof useQueryClient>,
  taskId: string,
) {
  queryClient.setQueriesData(
    { queryKey: MY_TASKS_QUERY_KEY },
    (
      oldData: { tasks: TaskWithAssignments[]; totalCount: number } | undefined,
    ) => {
      if (!oldData?.tasks || !oldData.tasks.some(task => task.id === taskId))
        return oldData
      return {
        ...oldData,
        tasks: oldData.tasks.filter(task => task.id !== taskId),
        totalCount: Math.max(0, oldData.totalCount - 1),
      }
    },
  )
}
